import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  feeRuleCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { feeEngineEndpoints } from "@/features/fee-engine/api/endpoints";
import type { FeeRuleDto } from "@/features/fee-engine/api/dto";

/**
 * Archive une règle de frais (`POST /FeeRules/{id}/archive`, guide §6.5) —
 * la règle passe en ARCHIVED et n'est plus proposée par le devis.
 */
export const useArchiveFeeRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["fee-engine", "fee-rules", "archive"],
    mutationFn: (feeRuleId: string) =>
      mutateData({
        url: feeEngineEndpoints.feeRules.archive(feeRuleId),
        method: "POST",
      }) as Promise<FeeRuleDto>,
    onSuccess: (_data, feeRuleId) => {
      return invalidateAfterMutation(queryClient, feeRuleCacheKeys(feeRuleId));
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useArchiveFeeRule failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "fee-engine" }
      );
    },
  });
};
